import { useState, useEffect, useRef, useMemo, memo } from 'react';
import Map, { Marker, Popup, NavigationControl, AttributionControl } from 'react-map-gl';
import Supercluster from 'supercluster';
import PropTypes from 'prop-types';
import 'mapbox-gl/dist/mapbox-gl.css';

// Get Mapbox key from env (dev) or window config (production)
const MAPBOX_API_KEY = import.meta.env.VITE_MAPBOX_API_KEY || window.APP_CONFIG?.MAPBOX_API_KEY;

// Debug: Check if token is loaded
if (!MAPBOX_API_KEY) {
  console.error('MAPBOX_API_KEY is not defined. Please check your .env file or runtime config.');
}

// Helper function to normalize different marker data formats
const normalizeMarkerData = (markers) => {
  if (!markers || !Array.isArray(markers)) {
    console.warn('Invalid markers data:', markers);
    return [];
  }

  const normalizedMarkers = markers.map((marker, index) => {
    let position;
    let text;
    let color = marker.color || 'blue';

    // Format 1: { position: [lat, lon], text: "label" }
    if (marker.position && Array.isArray(marker.position)) {
      position = marker.position;
      text = marker.text || marker.name || 'Location';
    }
    // Format 2: { lat: number, lon: number, name: "label" }
    else if (typeof marker.lat !== 'undefined' && typeof marker.lon !== 'undefined') {
      position = [marker.lat, marker.lon];
      text = marker.name || marker.text || 'Location';
    }
    // Format 3: { latitude: number, longitude: number, name: "label" }
    else if (typeof marker.latitude !== 'undefined' && typeof marker.longitude !== 'undefined') {
      position = [marker.latitude, marker.longitude];
      text = marker.name || marker.text || 'Location';
    }
    // Format 4: [lat, lon, "label"]
    else if (Array.isArray(marker) && marker.length >= 2) {
      position = [marker[0], marker[1]];
      text = marker[2] || 'Location';
    }
    // Default case
    else {
      position = Array.isArray(marker) ? marker.slice(0, 2) : [0, 0];
      text = (typeof marker === 'object' && marker !== null)
        ? (marker.text || marker.name || marker.label || 'Location')
        : 'Location';
    }

    return {
      type: 'Feature',
      properties: {
        cluster: false,
        markerId: index,
        text,
        color
      },
      geometry: {
        type: 'Point',
        coordinates: [position[1], position[0]] // [lng, lat] for GeoJSON
      }
    };
  });

  // Filter out invalid markers
  const validMarkers = normalizedMarkers.filter(marker => {
    const coords = marker.geometry.coordinates;
    return (
      Array.isArray(coords) &&
      coords.length === 2 &&
      !isNaN(coords[0]) &&
      !isNaN(coords[1]) &&
      coords[0] >= -180 && coords[0] <= 180 &&
      coords[1] >= -90 && coords[1] <= 90
    );
  });

  if (normalizedMarkers.length > 0 && validMarkers.length === 0) {
    console.error('All markers were invalid:', markers);
  } else if (normalizedMarkers.length !== validMarkers.length) {
    console.warn(`Filtered out ${normalizedMarkers.length - validMarkers.length} invalid markers`);
  }

  return validMarkers;
};

// Calculate bounds and zoom from markers
const calculateBounds = (markers) => {
  if (!markers || markers.length === 0) {
    return { center: [0, 0], zoom: 2 };
  }

  let minLat = 90, maxLat = -90, minLng = 180, maxLng = -180;

  markers.forEach(marker => {
    const [lng, lat] = marker.geometry.coordinates;
    minLat = Math.min(minLat, lat);
    maxLat = Math.max(maxLat, lat);
    minLng = Math.min(minLng, lng);
    maxLng = Math.max(maxLng, lng);
  });

  const center = [(minLng + maxLng) / 2, (minLat + maxLat) / 2];
  const latSpan = maxLat - minLat;
  const lngSpan = maxLng - minLng;
  const maxSpan = Math.max(latSpan, lngSpan);

  // Calculate zoom based on span
  let zoom = 2;
  if (maxSpan > 150) zoom = 1;
  else if (maxSpan > 120) zoom = 2;
  else if (maxSpan > 90) zoom = 3;
  else if (maxSpan > 60) zoom = 4;
  else if (maxSpan > 30) zoom = 5;
  else if (maxSpan > 15) zoom = 6;
  else if (maxSpan > 8) zoom = 7;
  else if (maxSpan > 4) zoom = 8;
  else if (maxSpan > 2) zoom = 9;
  else if (maxSpan > 1) zoom = 10;
  else if (maxSpan > 0.5) zoom = 11;
  else if (maxSpan > 0.2) zoom = 12;
  else if (maxSpan > 0.1) zoom = 13;
  else if (maxSpan > 0.05) zoom = 14;
  else if (maxSpan > 0.025) zoom = 15;
  else if (maxSpan > 0.0125) zoom = 16;
  else zoom = 17;

  // Adjust zoom slightly based on number of markers
  if (markers.length > 100) zoom = Math.max(1, zoom - 1);

  return { center, zoom };
};

// Color mapping for markers
const markerColors = {
  blue: '#3B82F6',
  red: '#EF4444',
  green: '#10B981',
  orange: '#F97316',
  yellow: '#EAB308',
  violet: '#8B5CF6',
  grey: '#6B7280',
  black: '#000000'
};

const MapView = memo(({ data }) => {
  const [viewState, setViewState] = useState({
    longitude: 0,
    latitude: 0,
    zoom: 2
  });

  const [markers, setMarkers] = useState([]);
  const [selectedMarker, setSelectedMarker] = useState(null);
  const [clusters, setClusters] = useState([]);
  const [mapLoaded, setMapLoaded] = useState(false);
  const [markerBounds, setMarkerBounds] = useState(null);
  const mapRef = useRef();

  // Initialize supercluster
  const supercluster = useMemo(() => {
    const cluster = new Supercluster({
      radius: 75,
      maxZoom: 20
    });
    return cluster;
  }, []);

  // Process incoming data
  useEffect(() => {
    if (!data) {
      console.warn('No data provided to MapView');
      return;
    }

    let center;
    let zoom;
    let markersData = [];

    if (typeof data === 'object' && !Array.isArray(data)) {
      // Object with center, zoom, and markers
      if (data.center) {
        if (Array.isArray(data.center) && data.center.length === 2) {
          center = [data.center[1], data.center[0]]; // [lng, lat]
        } else if (data.center.lat && data.center.lon) {
          center = [data.center.lon, data.center.lat];
        }
      }

      if (typeof data.zoom === 'number') {
        zoom = data.zoom;
      }

      if (data.markers && Array.isArray(data.markers)) {
        markersData = data.markers;
      }
    } else if (Array.isArray(data)) {
      markersData = data;
    }

    // Normalize markers
    const normalizedMarkers = normalizeMarkerData(markersData);
    setMarkers(normalizedMarkers);

    // Calculate bounds for fitBounds
    if (normalizedMarkers.length > 0) {
      let minLng = 180, maxLng = -180, minLat = 90, maxLat = -90;
      normalizedMarkers.forEach(marker => {
        const [lng, lat] = marker.geometry.coordinates;
        minLng = Math.min(minLng, lng);
        maxLng = Math.max(maxLng, lng);
        minLat = Math.min(minLat, lat);
        maxLat = Math.max(maxLat, lat);
      });
      setMarkerBounds([[minLng, minLat], [maxLng, maxLat]]);
    }

    // Calculate bounds if not explicitly provided
    if (!center || !zoom) {
      const bounds = calculateBounds(normalizedMarkers);
      if (!center) center = bounds.center;
      if (!zoom) zoom = bounds.zoom;
    }

    setViewState({
      longitude: center[0],
      latitude: center[1],
      zoom
    });
  }, [data]);

  // Fit bounds when markers or map changes
  useEffect(() => {
    if (!mapLoaded || !markerBounds || !mapRef.current) return;

    const map = mapRef.current.getMap();
    if (map) {
      map.fitBounds(markerBounds, {
        padding: { top: 50, bottom: 50, left: 50, right: 50 },
        maxZoom: 15,
        duration: 1000
      });
    }
  }, [mapLoaded, markerBounds]);

  // Update clusters when markers or viewport changes
  useEffect(() => {
    if (!mapLoaded || markers.length === 0) return;

    supercluster.load(markers);

    const map = mapRef.current?.getMap();
    if (!map) return;

    const bounds = map.getBounds();
    if (!bounds) return;

    const zoom = Math.floor(viewState.zoom);
    const clustersData = supercluster.getClusters(
      [bounds.getWest(), bounds.getSouth(), bounds.getEast(), bounds.getNorth()],
      zoom
    );

    setClusters(clustersData);
  }, [markers, viewState, supercluster, mapLoaded]);

  return (
    <div className="h-[400px] w-full rounded-lg overflow-hidden border border-gray-200 mapbox-container">
      <Map
        ref={mapRef}
        {...viewState}
        onMove={evt => setViewState(evt.viewState)}
        onLoad={() => {
          setMapLoaded(true);
          // Fit bounds to show all markers with padding
          if (markerBounds && mapRef.current) {
            const map = mapRef.current.getMap();
            map.fitBounds(markerBounds, {
              padding: { top: 50, bottom: 50, left: 50, right: 50 },
              maxZoom: 15
            });
          }
        }}
        mapboxAccessToken={MAPBOX_API_KEY}
        style={{ width: '100%', height: '100%' }}
        mapStyle="mapbox://styles/mapbox/light-v11"
        logoPosition="bottom-right"
        attributionControl={false}
      >
        <NavigationControl position="top-right" />
        <AttributionControl compact={true} style={{ opacity: 0.5 }} />

        {clusters.map((cluster) => {
          const [longitude, latitude] = cluster.geometry.coordinates;
          const { cluster: isCluster, point_count: pointCount } = cluster.properties;

          if (isCluster) {
            // Render cluster
            return (
              <Marker
                key={`cluster-${cluster.id}`}
                longitude={longitude}
                latitude={latitude}
                onClick={() => {
                  const expansionZoom = Math.min(
                    supercluster.getClusterExpansionZoom(cluster.id),
                    20
                  );
                  setViewState({
                    ...viewState,
                    longitude,
                    latitude,
                    zoom: expansionZoom
                  });
                }}
              >
                <div
                  style={{
                    width: 40,
                    height: 40,
                    borderRadius: '50%',
                    backgroundColor: 'rgba(128, 128, 128, 0.8)',
                    border: '2px solid white',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'black',
                    fontWeight: 'bold',
                    cursor: 'pointer'
                  }}
                >
                  {pointCount}
                </div>
              </Marker>
            );
          }

          // Render individual marker
          const color = markerColors[cluster.properties.color] || markerColors.blue;
          return (
            <Marker
              key={`marker-${cluster.properties.markerId}`}
              longitude={longitude}
              latitude={latitude}
              onClick={(e) => {
                e.originalEvent.stopPropagation();
                setSelectedMarker(cluster);
              }}
            >
              <div
                style={{
                  width: 12,
                  height: 12,
                  borderRadius: '50% 50% 50% 0',
                  backgroundColor: color,
                  border: '2px solid white',
                  transform: 'rotate(-45deg)',
                  cursor: 'pointer'
                }}
              />
            </Marker>
          );
        })}

        {selectedMarker && (
          <Popup
            longitude={selectedMarker.geometry.coordinates[0]}
            latitude={selectedMarker.geometry.coordinates[1]}
            onClose={() => setSelectedMarker(null)}
            closeButton={true}
            closeOnClick={false}
          >
            <div>{selectedMarker.properties.text}</div>
          </Popup>
        )}

        {markers.length === 0 && (
          <div
            style={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              backgroundColor: 'rgba(255, 255, 255, 0.9)',
              padding: '12px 20px',
              borderRadius: '8px',
              boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
              pointerEvents: 'none',
              fontSize: '14px',
              color: '#666'
            }}
          >
            No valid location data available
          </div>
        )}
      </Map>
    </div>
  );
}, (prevProps, nextProps) => {
  // Custom comparison: only re-render if data actually changed
  // Compare markers length and first/last marker positions as a quick check
  const prevMarkers = prevProps.data?.markers || [];
  const nextMarkers = nextProps.data?.markers || [];

  if (prevMarkers.length !== nextMarkers.length) return false;
  if (prevMarkers.length === 0) return true;

  // Compare first and last markers as a quick check
  const prevFirst = prevMarkers[0];
  const nextFirst = nextMarkers[0];
  const prevLast = prevMarkers[prevMarkers.length - 1];
  const nextLast = nextMarkers[nextMarkers.length - 1];

  const firstSame = JSON.stringify(prevFirst?.position) === JSON.stringify(nextFirst?.position);
  const lastSame = JSON.stringify(prevLast?.position) === JSON.stringify(nextLast?.position);

  return firstSame && lastSame;
});

MapView.propTypes = {
  data: PropTypes.oneOfType([
    PropTypes.shape({
      center: PropTypes.oneOfType([
        PropTypes.arrayOf(PropTypes.number),
        PropTypes.shape({
          lat: PropTypes.number,
          lon: PropTypes.number
        })
      ]),
      zoom: PropTypes.number,
      markers: PropTypes.array
    }),
    PropTypes.array
  ])
};

MapView.defaultProps = {
  data: {
    center: [0, 0],
    zoom: 2,
    markers: []
  }
};

export default MapView;
